import ContentTitle from '../../components/content/ContentTitle';
import AppUiScope from '../../app-ui/AppUiScope';
import baseline, { appTextStyle } from '../../app-ui/baseline';
import SupportPromotionCard from '../../features/sponsorship/SupportPromotionCard';
import { HomeEvents } from '../../features/events/EventContent';
import DividedOpinionCard from './DividedOpinionCard';
import HomeHero from '../../features/banners/HomeHero';
import RecentContent from '../../features/recent/RecentContentShelf';
import { useSponsoredPlacements } from '../../features/ads/useSponsoredPlacements';
import { SponsoredCollections } from '../../features/ads/SponsoredContent';
import ContentShelf, { ShelfNavigationContext } from '../../components/responsive/ContentShelf';
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useBrowseSession, useBrowsePosition } from '../../features/browse/useBrowseList';
import useExploreShelves from '../explore/useExploreShelves';
import ExploreState from '../explore/ExploreState';
import { useNearby } from '../../features/discovery/useNearby';
import { Link } from 'react-router-dom';
import { useQuery, useQueries } from '@tanstack/react-query';
import { discoveryApi } from '../../api/discovery';
import { useUserStore } from '../../stores/userStore';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { DiscoverySection, albumPath } from '../../components/content/DiscoveryContent';
import ProfileIdentity from '../../components/content/ProfileIdentity';
import {
  homeMemberReason,
  homeMemberActivity,
  homeMemberCandidates,
} from '../../features/discovery/homeMembers';
import { useHomeCandidates } from '../../features/discovery/useHomeCandidates';
import { useHomeSelection } from '../../features/discovery/useHomeSelection';
import useHomeWarmup from './useHomeWarmup';
import { readHomeSnapshot } from './homeSnapshot';
import { DiscoveryAlbums } from '../../features/discovery/DiscoveryCards';
import {
  dividedCandidates,
  dividedFallbackCandidates,
  dividedReviews,
  trackReason,
} from '../../features/discovery/presentation';
import {
  RecommendationTracking,
  RecommendationSlot,
  recommendationEvent,
  useRecommendationTracking,
} from '../../features/discovery/RecommendationTracking';
import { useTrackFeedbackUndo } from '../../features/discovery/useTrackFeedbackUndo';
import RecommendationFeedback from '../../features/discovery/RecommendationFeedback';
import { PreviewButton } from '../../features/preview/PreviewControls';
import { previewTrack } from '../../features/preview/previewPlayer';
import '../../styles/AppParity.css';
import './HomeDesign.css';
export function Tracks({ tracks, data, onExcluded, onFeedbackApplied, feedbackBusy, owner }) {
  const { t, locale } = useWebLocale();
  const { record } = useRecommendationTracking();
  const queue = useMemo(() => tracks.map((track) => previewTrack(track)), [tracks]);
  return (
    <div className="pp-recommended-tracks">
      {tracks.map((track, index) => {
        const event = recommendationEvent(data, 'HOME', 'TRACKS', track, index + 1);
        return (
          <RecommendationSlot event={event} key={track.id} className="pp-recommended-track">
            <div className="pp-track-cover">
              <img
                className="pp-track-art"
                src={track.albumImageUrl || undefined}
                alt=""
                loading="lazy"
              />
              <PreviewButton
                showProgress
                track={queue[index]}
                queue={queue}
                owner={owner}
                onPlay={() =>
                  record([
                    { ...event, eventType: 'RECOMMENDATION_CLICK' },
                    { ...event, eventType: 'PREVIEW_PLAY' },
                  ])
                }
              />
            </div>
            <Link
              to={albumPath({ albumId: track.albumId, albumSlug: track.albumSlug })}
              onClick={() =>
                record([
                  { ...event, eventType: 'RECOMMENDATION_CLICK' },
                  { ...event, eventType: 'ALBUM_OPEN' },
                ])
              }
            >
              <ContentTitle
                title={track.title}
                explicit={track.contentRating === 'EXPLICIT'}
                style={appTextStyle(baseline.contentText.trackTitle)}
              />
              <span className="pp-secondary" style={appTextStyle(baseline.contentText.artist)}>
                {track.artistName}
              </span>
              <span className="pp-discovery-reason">{trackReason(track, t, locale)}</span>
            </Link>
            <RecommendationFeedback
              item={track}
              disabled={feedbackBusy}
              kind="tracks"
              event={event}
              onExcluded={onExcluded}
              onFeedbackApplied={
                onFeedbackApplied ? (item, type) => onFeedbackApplied(item, type, index) : undefined
              }
            />
          </RecommendationSlot>
        );
      })}
    </div>
  );
}
function DividedOpinion({ home, rotation, rotationKey, identity, entry, onReady }) {
  const { t } = useWebLocale();
  const proposed = dividedCandidates(home, rotation)[0];
  if (entry.homeDivided?.key !== rotationKey && proposed) {
    entry.homeDivided = { key: rotationKey, candidate: proposed };
  }
  const candidate = entry.homeDivided?.key === rotationKey ? entry.homeDivided.candidate : null;
  const primary = useQuery({
    queryKey: ['web-divided-album', candidate?.albumId, identity],
    queryFn: ({ signal }) => discoveryApi.album(candidate, signal),
    enabled: Boolean(candidate),
    staleTime: 300000,
    retry: false,
  });
  const primaryPair = dividedReviews(primary.data);
  const needsFallback = !candidate || primary.isError || (primary.isSuccess && !primaryPair);
  const candidates = useMemo(
    () => dividedFallbackCandidates(home, rotation, candidate?.albumId),
    [home, rotation, candidate?.albumId],
  );
  const fallbacks = useQueries({
    queries: candidates.map((item) => ({
      queryKey: ['web-divided-album', item.albumId, identity],
      queryFn: ({ signal }) => discoveryApi.album(item, signal),
      enabled: needsFallback,
      staleTime: 300000,
      retry: false,
    })),
  });
  const queries = [...(candidate ? [primary] : []), ...(needsFallback ? fallbacks : [])];
  const ready = queries.every((q) => !q.isPending && !q.isFetching);
  useEffect(() => {
    onReady(ready);
  }, [onReady, ready]);
  const match = queries
    .map((query) => ({ detail: query.data, pair: dividedReviews(query.data) }))
    .find((item) => item.pair);
  return (
    <DiscoverySection title={t('home.dividedOpinion')}>
      {match ? (
        <DividedOpinionCard detail={match.detail} pair={match.pair} />
      ) : queries.some((query) => query.isPending) ? (
        <p role="status">{t('loading')}…</p>
      ) : queries.some((query) => query.isError) ? (
        <div role="alert">
          <p>{t('error')}</p>
          <button
            className="pp-action"
            onClick={() =>
              queries.filter((query) => query.isError).forEach((query) => query.refetch())
            }
          >
            {t('retry')}
          </button>
        </div>
      ) : (
        <p className="pp-secondary">{t('home.opinionWaitingBody')}</p>
      )}
    </DiscoverySection>
  );
}
function MemberCard({ member, authenticated, position, requestId, identity }) {
  const [nearRef, nearby] = useNearby();
  const { t } = useWebLocale();
  const reason = homeMemberReason(member);
  const reviews = useQuery({
    queryKey: ['web-member-reviews', authenticated, member.id, identity],
    enabled: nearby && !reason,
    queryFn: ({ signal }) =>
      discoveryApi.memberReviews(member.nickname, Boolean(authenticated), signal),
    staleTime: 300000,
    retry: false,
  });
  const { record } = useRecommendationTracking();
  const event = {
    targetUserId: member.id,
    surface: 'HOME',
    section: 'BROWSE_REVIEWERS',
    algorithmVersion: 'taste-social-v2',
    reason: member.recommendationReason || 'RECENT_REVIEWER',
    requestId,
    resultPosition: position,
  };
  return (
    <RecommendationSlot event={event}>
      <div ref={nearRef}>
        <Link
          className="pp-member-card"
          to={`/users/by-id/${member.id}`}
          onClick={() =>
            record([
              { ...event, eventType: 'RECOMMENDATION_IMPRESSION' },
              { ...event, eventType: 'RECOMMENDATION_CLICK', context: 'home_browse_reviewers' },
            ])
          }
        >
          <span className="pp-home-member-avatar" aria-hidden="true">
            {member.profileImageUrl ? (
              <img src={member.profileImageUrl} alt="" loading="lazy" />
            ) : (
              member.nickname?.slice(0, 1)
            )}
          </span>
          <ProfileIdentity name={member.nickname} mark={member.profileMark} avatar={false} />
          <span className="pp-home-member-reason pp-secondary">
            {homeMemberActivity(member, reviews.data || [], t)}
          </span>
        </Link>
      </div>
    </RecommendationSlot>
  );
}
function Members({ user, home, entry, identity, onReady }) {
  const [nearRef, observed] = useNearby();
  const nearby = observed || Boolean(entry.homeMembersEnabled);
  if (nearby) entry.homeMembersEnabled = true;
  const requestId = useRef(
    `home-reviewers:taste-social-v2:${user?.id || 'guest'}:${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
  ).current;
  const { t } = useWebLocale();
  const recommendations = useQuery({
    queryKey: ['web-members', user?.id, identity],
    queryFn: ({ signal }) => discoveryApi.members(signal),
    enabled: nearby && Boolean(user?.id),
    staleTime: 300000,
    retry: false,
  });
  const following = useQuery({
    queryKey: ['web-following', user?.id, identity],
    queryFn: ({ signal }) => discoveryApi.following(user.nickname, signal),
    enabled: nearby && Boolean(user?.id && user.nickname),
    staleTime: 300000,
    retry: false,
  });
  const recent = useQuery({
    queryKey: ['web-reviewer-candidates', identity],
    enabled: nearby,
    queryFn: ({ signal }) => discoveryApi.reviewerCandidates(signal),
    staleTime: 300000,
    retry: false,
  });
  const latestEnabled = nearby && recent.isError && recent.data == null;
  const latest = useQuery({
    queryKey: ['web-reviewer-candidates', identity, 30],
    queryFn: ({ signal }) => discoveryApi.reviewerCandidates(signal, 30),
    enabled: latestEnabled,
    staleTime: 300000,
    retry: false,
  });
  const items = homeMemberCandidates({
    recommendations: recommendations.data,
    reviews: recent.data ?? latest.data ?? home?.latestReviews,
    following: following.data,
    userId: user?.id,
    recommendationsPending: recommendations.isPending,
    followingPending: Boolean(user?.nickname) && following.isPending,
  });
  const pending =
    !nearby ||
    (!items.length &&
      ((user?.id &&
        (recommendations.isPending || (Boolean(user.nickname) && following.isPending))) ||
        recent.isPending ||
        (latestEnabled && latest.isPending)));
  const fetching =
    recommendations.isFetching ||
    following.isFetching ||
    recent.isFetching ||
    (latestEnabled && latest.isFetching);
  const failed =
    recommendations.isError ||
    following.isError ||
    recent.isError ||
    (latestEnabled && latest.isError);
  const ready = !nearby || (!pending && !fetching);
  useEffect(() => {
    onReady(ready);
  }, [onReady, ready]);
  return (
    <div
      ref={nearRef}
      className="pp-home-members-group"
      style={!pending && !items.length && !failed ? { display: 'none' } : undefined}
    >
      {(pending || items.length > 0 || failed) && (
        <DiscoverySection title={t('home.browseReviewers')}>
          {pending ? (
            <p role="status">{t('loading')}…</p>
          ) : items.length ? (
            <ContentShelf profile="members" label={t('home.browseReviewers')}>
              {items.map((member, index) => (
                <MemberCard
                  key={member.id}
                  member={member}
                  position={index + 1}
                  requestId={requestId}
                  authenticated={user?.id}
                  identity={identity}
                />
              ))}
            </ContentShelf>
          ) : (
            <p className="pp-secondary">{t('empty')}</p>
          )}
          {failed && (
            <button
              className="pp-action"
              disabled={fetching}
              onClick={() => {
                if (user?.id) {
                  recommendations.refetch();
                  if (user.nickname) following.refetch();
                }
                recent.refetch();
                if (latestEnabled) latest.refetch();
              }}
            >
              {t('retry')}
            </button>
          )}
        </DiscoverySection>
      )}
      {!pending && items.length > 0 && (
        <SupportPromotionCard surface="HOME" afterSection="reviewers" />
      )}
    </div>
  );
}
function HomeForAccount({ user, authReady, homeQuery, entry, returning, loadDiscovery }) {
  const campaigns = useSponsoredPlacements('HOME_DISCOVERY');
  const { t, locale } = useWebLocale();
  const scope = user?.id ? `user_${user.id}` : 'guest';
  const identity = [entry.version, locale];
  const [dividedReady, setDividedReady] = useState(false);
  const [membersReady, setMembersReady] = useState(!entry.homeMembersEnabled);
  const snapshot = useMemo(
    () => (authReady ? readHomeSnapshot(user?.id, locale) : undefined),
    [authReady, user?.id, locale],
  );
  const query = useQuery({
    initialData: snapshot?.data,
    initialDataUpdatedAt: snapshot?.at,
    queryKey: ['web-discovery', user?.id || 'guest', 'home', locale, entry.version],
    enabled: authReady,
    queryFn: ({ signal }) => loadDiscovery(user?.id, signal),
    staleTime: 300000,
    gcTime: 30 * 60000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const candidates = useHomeCandidates(query, homeQuery, {
    userId: user?.id,
    locale,
    version: entry.version,
  });
  const selection = useHomeSelection(scope, candidates.data, () => query.refetch());
  const trackFeedback = useTrackFeedbackUndo(selection.restoreTrack);
  const position = useBrowsePosition({
    entry,
    returning,
    ready:
      !homeQuery.isPending &&
      !homeQuery.isFetching &&
      !candidates.query.isPending &&
      !candidates.query.isFetching &&
      (!homeQuery.data || dividedReady) &&
      membersReady,
  });
  useExploreShelves(position.ref, entry, returning);
  return (
    <div {...position}>
      {trackFeedback.toast}
      <AppUiScope className="pp-parity pp-personalized-home pp-home-design" surface="home">
        <h1 className="pp-visually-hidden">{t('home')}</h1>
        <HomeHero />
        <RecommendationTracking
          key={scope}
          userId={user?.id}
          onImpression={(event) => {
            if (event.section === 'NEW_DISCOVERY') selection.mark(event.albumId, 'seen');
          }}
        >
          {(query.isPending || (query.data != null && selection.tracks.length > 0)) && (
            <DiscoverySection title={t('home.tracksForYou')}>
              <ExploreState query={query}>
                {selection.tracks.length ? (
                  <Tracks
                    owner={`home:${scope}`}
                    tracks={selection.tracks}
                    data={query.data}
                    onExcluded={selection.removeTrack}
                    onFeedbackApplied={trackFeedback.applied}
                    feedbackBusy={trackFeedback.busy}
                  />
                ) : (
                  <p className="pp-secondary">{t('empty')}</p>
                )}
              </ExploreState>
            </DiscoverySection>
          )}
          <DiscoverySection title={t('home.newDiscovery')}>
            <ExploreState query={candidates.query}>
              {selection.albums.length || campaigns.length ? (
                <DiscoveryAlbums
                  campaigns={campaigns}
                  surface="HOME"
                  items={selection.albums}
                  data={query.data}
                  onClick={(id) => selection.mark(id, 'clicked')}
                />
              ) : (
                <p className="pp-secondary">{t('empty')}</p>
              )}
            </ExploreState>
          </DiscoverySection>
          {selection.albums.length > 0 && (
            <SupportPromotionCard surface="HOME" afterSection="discovery" />
          )}
          <ExploreState query={homeQuery}>
            <DividedOpinion
              home={homeQuery.data}
              rotation={selection.albumRotation}
              rotationKey={selection.albumRotationKey}
              entry={entry}
              identity={identity}
              onReady={setDividedReady}
            />
          </ExploreState>
          <SponsoredCollections placementKey="HOME_DIVIDED_OPINION" />
          <Members
            user={user}
            home={homeQuery.data}
            entry={entry}
            identity={identity}
            onReady={setMembersReady}
          />
          <HomeEvents />
          <RecentContent identity={identity} />
        </RecommendationTracking>
      </AppUiScope>
    </div>
  );
}
export default function PersonalizedHome({ homeQuery }) {
  const user = useUserStore((state) => state.user);
  const loading = useUserStore((state) => state.loading);
  const initialized = useUserStore((state) => state.initialized);
  // Do not send a guest recommendation request while restoring a member session.
  const authReady = !loading && (initialized || !localStorage.getItem('POSTPOP_AUTH_TOKEN'));
  const { locale } = useWebLocale();
  const memory = useBrowseSession('home');
  const loadDiscovery = useHomeWarmup({ authReady, locale });
  return (
    <ShelfNavigationContext.Provider value="edges">
      <HomeForAccount
        key={`${user?.id || 'guest'}:${locale}:${memory.entry.version}:${memory.location.key}`}
        user={user}
        authReady={authReady}
        loadDiscovery={loadDiscovery}
        homeQuery={homeQuery}
        {...memory}
      />
    </ShelfNavigationContext.Provider>
  );
}
